import React, { useState, useEffect } from "react";
import { Box, Stack, Text, Center, Group } from "@mantine/core";

export interface AppErrorScreenProps {
  /** Main error title */
  title?: string;

  /** User-friendly error message */
  message?: string;

  /** Technical error details (will be shown in monospace) */
  error?: string;

  /** Emoji to display (default: 🤔) */
  emoji?: string;

  /** Humorous text to lighten the mood */
  humorText?: string;

  /** Additional CSS class name */
  className?: string;

  /** Whether to show auto-retry information */
  showRetryInfo?: boolean;
}

export const AppErrorScreen: React.FC<AppErrorScreenProps> = ({
  title = "Oops! Something went wrong",
  message,
  error,
  emoji = "🤔",
  humorText = "Even the best of us have bad days...",
  className,
  showRetryInfo = true,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box
      className={`app-error-screen ${className || ""}`}
      h="100%"
      style={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.gray[0],
        padding: theme.spacing.xl,
        position: "relative",
      })}
    >
      {/* Date and Time Display */}
      <Box
        className="app-error-screen__datetime"
        style={(theme) => ({
          position: "absolute",
          top: theme.spacing.xl,
          right: theme.spacing.xl,
          textAlign: "right",
        })}
      >
        <Text
          size="lg"
          fw={300}
          style={(theme) => ({
            color: theme.colors.gray[7],
            lineHeight: 1.2,
          })}
        >
          {formatTime(currentTime)}
        </Text>
        <Text
          size="sm"
          style={(theme) => ({
            color: theme.colors.gray[5],
            marginTop: 2,
          })}
        >
          {formatDate(currentTime)}
        </Text>
      </Box>

      <Center>
        <Stack align="center" gap="xl" style={{ maxWidth: 600, width: "100%" }}>
          {/* Emoji */}
          <Text
            className="app-error-screen__emoji"
            size="80px"
            style={{
              lineHeight: 1,
              userSelect: "none",
            }}
          >
            {emoji}
          </Text>

          {/* Main content */}
          <Stack align="center" gap="md">
            {/* Title */}
            <Text
              className="app-error-screen__title"
              size="xl"
              fw={400}
              ta="center"
              style={(theme) => ({
                color: theme.colors.gray[8],
                lineHeight: 1.3,
              })}
            >
              {title}
            </Text>

            {/* Humor text */}
            <Text
              className="app-error-screen__humor"
              size="md"
              ta="center"
              style={(theme) => ({
                color: theme.colors.gray[6],
                fontWeight: 300,
              })}
            >
              {humorText}
            </Text>

            {/* Error message */}
            {message && (
              <Text
                className="app-error-screen__message"
                size="md"
                ta="center"
                style={(theme) => ({
                  color: theme.colors.red[7],
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  marginTop: theme.spacing.md,
                  maxWidth: "100%",
                })}
              >
                {message}
              </Text>
            )}
          </Stack>

          {/* Technical error details */}
          {error && (
            <Box
              className="app-error-screen__error"
              style={(theme) => ({
                width: "100%",
                marginTop: theme.spacing.lg,
              })}
            >
              <Text
                size="xs"
                fw={500}
                tt="uppercase"
                ta="center"
                style={(theme) => ({
                  color: theme.colors.gray[6],
                  letterSpacing: "1px",
                  marginBottom: theme.spacing.sm,
                })}
              >
                Technical Details
              </Text>
              <Text
                className="app-error-screen__error-text"
                size="xs"
                ff="monospace"
                ta="center"
                style={(theme) => ({
                  color: theme.colors.gray[7],
                  backgroundColor: theme.colors.gray[1],
                  padding: theme.spacing.sm,
                  borderRadius: theme.radius.sm,
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                })}
              >
                {error}
              </Text>
            </Box>
          )}

          {/* Auto-retry info */}
          {showRetryInfo && (
            <Group
              className="app-error-screen__retry-info"
              gap="xs"
              justify="center"
              style={(theme) => ({
                marginTop: theme.spacing.xl,
              })}
            >
              <Text
                size="sm"
                style={(theme) => ({
                  color: theme.colors.blue[5],
                })}
              >
                🔄
              </Text>
              <Text
                size="sm"
                ta="center"
                style={(theme) => ({
                  color: theme.colors.gray[6],
                  fontWeight: 300,
                })}
              >
                We're working on it behind the scenes
              </Text>
            </Group>
          )}
        </Stack>
      </Center>
    </Box>
  );
};
